import { invoke } from '@tauri-apps/api/core';
import { listen } from '@tauri-apps/api/event';
import { useEffect, useRef } from 'react';
import { applyDesktopDeepLinkParams, redeemDesktopMagicToken } from 'services/desktop-session';
import { isTauriApp } from 'utils/is-tauri';
export const DESKTOP_AUTH_HANDOFF_EVENT = 'desktop-auth-handoff';
const dispatchHandoff = (detail)=>{
    if (typeof window === 'undefined') {
        return;
    }
    window.dispatchEvent(new CustomEvent(DESKTOP_AUTH_HANDOFF_EVENT, {
        detail
    }));
};
export const parseDeepLinkParams = (rawUrl)=>{
    try {
        // Custom schemes without a host can throw in some engines — normalize first.
        const normalized = rawUrl.startsWith('rocketlane:') && !rawUrl.includes('://') ? rawUrl.replace('rocketlane:', 'rocketlane://') : rawUrl;
        const url = new URL(normalized);
        const params = url.searchParams;
        if (url.hash.includes('?')) {
            const hashQuery = url.hash.slice(url.hash.indexOf('?') + 1);
            new URLSearchParams(hashQuery).forEach((value, key)=>{
                if (!params.has(key)) {
                    params.set(key, value);
                }
            });
        }
        return params;
    } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to parse desktop deep link', err);
        return null;
    }
};
/**
 * Cold-start URLs from the deep-link plugin (CLI args / launch URL).
 * Uses the plugin command directly so we don't need the JS package installed.
 */ const getCurrentDeepLinkUrls = async ()=>{
    try {
        const urls = await invoke('plugin:deep-link|get_current');
        return urls !== null && urls !== void 0 ? urls : [];
    } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('desktop deep-link get_current unavailable', err);
        return [];
    }
};
export const handleDeepLinkUrl = async (rawUrl)=>{
    const params = parseDeepLinkParams(rawUrl);
    if (!params) {
        return false;
    }
    // Prefer an explicit apiKey session deep link when present.
    if (applyDesktopDeepLinkParams(params)) {
        return true;
    }
    // Magic-link handoff: browser bounced to rocketlane://auth?token=…
    if (params.get('token')) {
        dispatchHandoff({
            status: 'pending',
            messageKey: 'desktop.auth.finishing_signin'
        });
        try {
            const ok = await redeemDesktopMagicToken(params);
            if (!ok) {
                dispatchHandoff({
                    status: 'error',
                    messageKey: 'desktop.auth.signin.link.error'
                });
                return false;
            }
            dispatchHandoff({
                status: 'success'
            });
            return true;
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Failed to redeem desktop magic token', err);
            if (err && typeof err === 'object') {
                var _maybeAxios_response;
                const maybeAxios = err;
                if ((_maybeAxios_response = maybeAxios.response) === null || _maybeAxios_response === void 0 ? void 0 : _maybeAxios_response.status) {
                    dispatchHandoff({
                        status: 'error',
                        messageKey: 'desktop.auth.signin.link.error.status',
                        messageValues: {
                            status: maybeAxios.response.status
                        }
                    });
                    return false;
                }
            }
            dispatchHandoff({
                status: 'error',
                messageKey: 'desktop.auth.signin.link.error.generic'
            });
            return false;
        }
    }
    return false;
};
/**
 * Listens for rocketlane:// deep links (cold start + while running).
 * Expected params: apiKey (+ optional account/apiHost), or token (+ optional agent/account).
 */ export const useDesktopDeepLinkHandler = ()=>{
    const handledUrlsRef = useRef(new Set());
    const inFlightRef = useRef(false);
    useEffect(()=>{
        if (!isTauriApp()) {
            return undefined;
        }
        let cancelled = false;
        const unlisteners = [];
        const markUrlHandled = (rawUrl)=>{
            handledUrlsRef.current.add(rawUrl);
            try {
                const key = '__desktop_handled_deeplinks';
                const prev = sessionStorage.getItem(key);
                const list = prev ? JSON.parse(prev) : [];
                if (!list.includes(rawUrl)) {
                    list.push(rawUrl);
                    sessionStorage.setItem(key, JSON.stringify(list.slice(-20)));
                }
            } catch (unused) {
            // ignore
            }
        };
        const wasUrlHandled = (rawUrl)=>{
            if (handledUrlsRef.current.has(rawUrl)) {
                return true;
            }
            try {
                const prev = sessionStorage.getItem('__desktop_handled_deeplinks');
                const list = prev ? JSON.parse(prev) : [];
                return list.includes(rawUrl);
            } catch (unused) {
                return false;
            }
        };
        const processUrl = async (rawUrl)=>{
            if (cancelled || !rawUrl) {
                return;
            }
            if (wasUrlHandled(rawUrl) || inFlightRef.current) {
                return;
            }
            inFlightRef.current = true;
            try {
                const ok = await handleDeepLinkUrl(rawUrl);
                if (cancelled) {
                    return;
                }
                if (ok) {
                    markUrlHandled(rawUrl);
                    window.location.replace('/');
                }
            } finally{
                inFlightRef.current = false;
            }
        };
        const processUrls = (urls)=>{
            for (const url of urls){
                void processUrl(url);
            }
        };
        // Warm path: plugin event while the app is already running (macOS/iOS).
        void listen('deep-link://new-url', (event)=>{
            if (!Array.isArray(event.payload)) {
                return;
            }
            processUrls(event.payload);
        }).then((fn)=>{
            if (cancelled) {
                fn();
                return;
            }
            unlisteners.push(fn);
        }).catch((err)=>{
            // eslint-disable-next-line no-console
            console.warn('desktop deep-link://new-url listener unavailable', err);
        });
        // Warm path (legacy): custom event from Rust on_open_url / setup emit.
        void listen('desktop-deep-link', (event)=>{
            if (!event.payload || typeof event.payload !== 'string') {
                return;
            }
            void processUrl(event.payload);
        }).then((fn)=>{
            if (cancelled) {
                fn();
                return;
            }
            unlisteners.push(fn);
        }).catch((err)=>{
            // eslint-disable-next-line no-console
            console.warn('desktop-deep-link listener unavailable', err);
        });
        // Cold start: URLs that launched the app before the FE listener was ready.
        void getCurrentDeepLinkUrls().then((urls)=>{
            if (cancelled || urls.length === 0) {
                return;
            }
            processUrls(urls);
        });
        return ()=>{
            cancelled = true;
            for (const unlisten of unlisteners){
                unlisten();
            }
        };
    }, []);
};
